import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "../../../foundation/altzero_base/base/components/ui/card";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import {
  ArrowRight,
  Clock,
  ArrowLeft,
  Star,
  Rocket,
  Brain,
  TrendingUp,
  Users,
  Target,
  Lightbulb,
  Globe,
  Zap,
  CheckCircle,
  Sparkles
} from "lucide-react";
import ScopingAILayout from "../components/ScopingAILayout";

const NewProposal: React.FC = () => {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tenderId = params.get('tender');

    if (tenderId) {
      // If resuming a tender, skip selection and go directly to workflow
      // We don't clear session storage here because we want to allow DocumentFormContext 
      // to handle the transition, and loadTenderData will overwrite state.
      navigate(`/tender/documents/new?tender=${tenderId}`, { replace: true });
      return;
    }

    setIsLoaded(true);
  }, []);

  const proposalOptions = [
    {
      id: "tendering",
      title: "Tendering Proposal",
      subtitle: "Full Workflow",
      description: "Complete tender management with client intake, offer validation, retailer comparison, and branded proposal generation",
      icon: Brain,
      secondaryIcon: Sparkles,
      color: "from-blue-500 via-indigo-600 to-purple-700",
      glowColor: "shadow-blue-500/25",
      features: [
        { icon: Users, text: "Client & site management" },
        { icon: CheckCircle, text: "Data validation (ABN/NMI)" },
        { icon: Globe, text: "Multi-retailer offers" },
        { icon: Star, text: "Branded PDF proposals" }
      ],
      stats: { time: "", success: "", users: "" },
      comingSoon: false,
      disabled: false,
      onClick: () => {
        sessionStorage.removeItem('scopingai_selectedClient');
        sessionStorage.removeItem('scopingai_projectInfo');
        navigate("/tender/documents/new");
      }
    },
    {
      id: "quick",
      title: "Quick Proposal",
      subtitle: "Side-by-Side Comparison",
      description: "Fast comparison view for shortlisting and broker conversations with instant side-by-side analysis",
      icon: Rocket,
      secondaryIcon: Zap,
      color: "from-amber-400 via-orange-500 to-red-500",
      glowColor: "shadow-orange-500/25",
      features: [
        { icon: Clock, text: "5-minute setup" },
        { icon: TrendingUp, text: "Side-by-side comparison" },
        { icon: Target, text: "Quick client conversations" },
        { icon: Lightbulb, text: "Instant insights" }
      ],
      stats: { time: "", success: "", users: "" },
      comingSoon: false,
      disabled: false,
      onClick: () => {
        sessionStorage.removeItem('scopingai_selectedClient');
        sessionStorage.removeItem('scopingai_projectInfo');
        navigate("/tender/quick-proposal");
      }
    }
  ];

  return (
    <ScopingAILayout>
      <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
        {/* Enhanced Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-600/20 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          <motion.div
            className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-br from-purple-400/20 to-pink-600/20 rounded-full blur-3xl"
            animate={{
              scale: [1.2, 1, 1.2],
              rotate: [360, 180, 0],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          <motion.div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-gradient-to-br from-indigo-400/10 to-cyan-600/10 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        {/* Professional Header Section */}
        <motion.div
          className="relative z-10 bg-white/90 backdrop-blur-xl border-b border-gray-200/50 shadow-xl"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="w-full px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <motion.div
                className="flex items-center gap-6"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Button
                  variant="ghost"
                  onClick={() => navigate('/tender/dashboard')}
                  className="gap-3 hover:bg-blue-50 hover:text-blue-700 transition-all duration-300 group px-4 py-2 rounded-xl border border-transparent hover:border-blue-200"
                >
                  <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform duration-300" />
                  <span className="font-medium">Back to Dashboard</span>
                </Button>
                <div className="h-8 w-px bg-gray-300"></div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Create New Proposal</h1>
                  <p className="text-sm text-gray-600 mt-1">Choose your proposal workflow to get started</p>
                </div>
              </motion.div>

              <motion.div
                className="hidden md:flex items-center gap-4"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Badge variant="secondary" className="px-3 py-1 bg-blue-50 text-blue-700 border-blue-200">
                  <Sparkles className="h-3 w-3 mr-1" />
                  AI-Powered
                </Badge>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Options Section - Full Width */}
        <div className="relative z-10 w-full px-6 lg:px-8 py-8">
          <motion.div
            className="w-full max-w-7xl mx-auto"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {/* Cards Grid - Full Width Responsive */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 lg:gap-12">
              {proposalOptions.map((option, index) => {
                const IconComponent = option.icon;
                const SecondaryIcon = option.secondaryIcon;

                return (
                  <motion.div
                    key={option.id}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 * index }}
                    whileHover={{
                      y: -10,
                      transition: { duration: 0.3 }
                    }}
                    onHoverStart={() => setHoveredCard(option.id)}
                    onHoverEnd={() => setHoveredCard(null)}
                  >
                    <Card
                      className={`relative overflow-hidden transition-all duration-500 group cursor-pointer h-full
                      ${option.disabled
                          ? 'opacity-60 cursor-not-allowed'
                          : `hover:shadow-2xl hover:${option.glowColor} border-0 bg-white/95 backdrop-blur-sm hover:bg-white`
                        }`}
                      onClick={option.disabled ? undefined : option.onClick}
                    >
                      {/* Enhanced Gradient Border Effect */}
                      <div className={`absolute inset-0 bg-gradient-to-r ${option.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl`}
                        style={{ padding: '3px' }}>
                        <div className="h-full w-full bg-white rounded-lg"></div>
                      </div>

                      {/* Subtle Background Pattern */}
                      <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-500">
                        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-gray-100 to-transparent"></div>
                      </div>

                      {/* Coming Soon Badge */}
                      {option.comingSoon && (
                        <motion.div
                          className="absolute top-4 right-4 z-20"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.5 + index * 0.1 }}
                        >
                          <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-0 shadow-lg">
                            <Clock className="h-3 w-3 mr-1" />
                            Coming Soon
                          </Badge>
                        </motion.div>
                      )}

                      {/* Enhanced Content Layout */}
                      <div className="relative z-10 p-8 h-full flex flex-col">
                        {/* Header with Icons */}
                        <div className="mb-8">
                          <div className="flex items-start justify-between mb-6">
                            <motion.div
                              className={`relative w-20 h-20 rounded-3xl flex items-center justify-center shadow-xl ${option.id === 'quick'
                                ? 'bg-gradient-to-br from-orange-400 to-orange-600'
                                : 'bg-gradient-to-br from-blue-500 to-blue-700'
                                }`}
                              whileHover={{ rotate: 360, scale: 1.05 }}
                              transition={{ duration: 0.6 }}
                            >
                              <IconComponent className="h-10 w-10 text-white" />
                              <motion.div
                                className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-gray-100"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.3 + index * 0.1 }}
                              >
                                <SecondaryIcon className={`h-4 w-4 ${option.id === 'quick'
                                  ? 'text-orange-600'
                                  : 'text-blue-600'
                                  }`} />
                              </motion.div>
                            </motion.div>

                            {/* Status Badge */}
                            <motion.div
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.5 + index * 0.1 }}
                            >
                              <Badge className={`px-3 py-1 text-xs font-medium ${option.id === 'quick'
                                ? 'bg-orange-50 text-orange-700 border-orange-200'
                                : 'bg-blue-50 text-blue-700 border-blue-200'
                                }`}>
                                {option.subtitle}
                              </Badge>
                            </motion.div>
                          </div>

                          <div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-4 leading-tight">
                              {option.title}
                            </h3>
                            <p className="text-gray-600 leading-relaxed text-base mb-2">
                              {option.description}
                            </p>
                          </div>
                        </div>

                        {/* Enhanced Features List */}
                        <div className="flex-1 mb-8">
                          <h4 className="text-lg font-semibold text-gray-800 mb-4">Key Features</h4>
                          <div className="grid grid-cols-1 gap-4">
                            {option.features.map((feature, featureIndex) => {
                              const FeatureIcon = feature.icon;
                              return (
                                <motion.div
                                  key={featureIndex}
                                  className="flex items-center gap-4 p-3 rounded-xl bg-gray-50/50 hover:bg-gray-100/50 transition-colors duration-200"
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.6 + featureIndex * 0.1 }}
                                >
                                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center shadow-sm ${option.id === 'quick'
                                    ? 'bg-gradient-to-br from-orange-400 to-orange-500'
                                    : 'bg-gradient-to-br from-blue-500 to-blue-600'
                                    }`}>
                                    <FeatureIcon className="h-4 w-4 text-white" />
                                  </div>
                                  <span className="text-gray-700 font-medium flex-1">{feature.text}</span>
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Enhanced Action Button */}
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="mt-auto"
                        >
                          <Button
                            className={`w-full h-14 text-white font-bold rounded-2xl transition-all duration-300 shadow-lg text-lg ${option.disabled
                              ? 'bg-gray-400 cursor-not-allowed'
                              : option.id === 'quick'
                                ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 hover:shadow-xl hover:shadow-orange-500/25'
                                : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 hover:shadow-xl hover:shadow-blue-500/25'
                              }`}
                            disabled={option.disabled}
                            onClick={option.disabled ? undefined : option.onClick}
                          >
                            {option.disabled ? (
                              <>
                                <Clock className="h-5 w-5 mr-3 text-white" />
                                Coming Soon
                              </>
                            ) : (
                              <>
                                {option.id === 'tendering' ? (
                                  <Brain className="h-5 w-5 mr-3 text-white" />
                                ) : (
                                  <Rocket className="h-5 w-5 mr-3 text-white" />
                                )}
                                Get Started
                                <ArrowRight className="h-5 w-5 ml-3 text-white group-hover:translate-x-1 transition-transform duration-300" />
                              </>
                            )}
                          </Button>
                        </motion.div>
                      </div>

                      {/* Enhanced Hover Effect Overlay */}
                      <motion.div
                        className={`absolute inset-0 bg-gradient-to-br ${option.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-xl`}
                        initial={false}
                      />
                    </Card>
                  </motion.div>
                );
              })}
            </div>


          </motion.div>
        </div>
      </div>
    </ScopingAILayout>
  );
};

export default NewProposal;
